import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserPlus, Trash2, Edit2, CheckCircle, XCircle } from 'lucide-react';
import type { Employee } from '@/types/auth';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export function EmployeeManagement() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    stateId: '',
    name: '',
    phoneNumber: '',
    position: '',
    salary: '',
    notes: '',
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (err) {
      setError('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const currentUser = JSON.parse(sessionStorage.getItem('lsba_user') || '{}');

    try {
      setLoading(true);

      if (editingId) {
        // Update employee
        const response = await fetch(`${API_BASE}/api/employees`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editingId,
            ...formData,
            salary: formData.salary ? parseFloat(formData.salary) : undefined,
          }),
        });

        if (response.ok) {
          setSuccess('Employee updated successfully');
          setEditingId(null);
          resetForm();
          await fetchEmployees();
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to update employee');
        }
      } else {
        // Create employee
        const response = await fetch(`${API_BASE}/api/employees`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            salary: formData.salary ? parseFloat(formData.salary) : undefined,
            createdBy: currentUser.username || 'admin',
          }),
        });

        if (response.ok) {
          setSuccess('Employee added successfully');
          resetForm();
          await fetchEmployees();
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to add employee');
        }
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employee: Employee) => {
    setEditingId(employee.id);
    setFormData({
      stateId: employee.stateId,
      name: employee.name,
      phoneNumber: employee.phoneNumber,
      position: employee.position,
      salary: employee.salary?.toString() || '',
      notes: employee.notes || '',
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this employee?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/employees`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });

      if (response.ok) {
        setSuccess('Employee deleted successfully');
        await fetchEmployees();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete employee');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      stateId: '',
      name: '',
      phoneNumber: '',
      position: '',
      salary: '',
      notes: '',
    });
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {editingId ? 'Edit Employee' : 'Add New Employee'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4 bg-green-50 text-green-900 border-green-200">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="stateId">State ID *</Label>
                <Input
                  id="stateId"
                  value={formData.stateId}
                  onChange={(e) => setFormData({ ...formData, stateId: e.target.value })}
                  required
                  disabled={!!editingId}
                  placeholder="Enter State ID"
                />
              </div>
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  required
                  placeholder="555-123-4567"
                />
              </div>
              <div>
                <Label htmlFor="position">Position *</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  required
                  placeholder="e.g., Recruiter, Manager"
                />
              </div>
              <div>
                <Label htmlFor="salary">Salary (Optional)</Label>
                <Input
                  id="salary"
                  type="number"
                  step="0.01"
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <textarea
                id="notes"
                className="w-full min-h-[80px] px-3 py-2 border rounded-md"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this employee"
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? 'Processing...' : editingId ? 'Update Employee' : 'Add Employee'}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Employees</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && !employees.length ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : employees.length === 0 ? (
            <p className="text-center text-gray-500">No employees yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">State ID</th>
                    <th className="text-left p-3">Phone</th>
                    <th className="text-left p-3">Position</th>
                    <th className="text-center p-3">Boxer</th>
                    <th className="text-center p-3">Sponsor</th>
                    <th className="text-left p-3">Hired Date</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => (
                    <tr key={employee.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{employee.name}</td>
                      <td className="p-3">{employee.stateId}</td>
                      <td className="p-3">{employee.phoneNumber}</td>
                      <td className="p-3">{employee.position}</td>
                      <td className="p-3 text-center">
                        {employee.isBoxer ? (
                          <CheckCircle className="h-5 w-5 text-green-600 inline" />
                        ) : (
                          <XCircle className="h-5 w-5 text-gray-400 inline" />
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {employee.isSponsor ? (
                          <CheckCircle className="h-5 w-5 text-blue-600 inline" />
                        ) : (
                          <XCircle className="h-5 w-5 text-gray-400 inline" />
                        )}
                      </td>
                      <td className="p-3">
                        {new Date(employee.hiredDate).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(employee)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(employee.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
